import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getCampaigns,
  getMetricsSummary,
  getTopPerformers,
  getAccounts,
  syncCampaigns,
} from "../services/api";

// ─── useAccounts ──────────────────────────────────────────────
export function useAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    getAccounts()
      .then((res) => setAccounts(res.accounts || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return { accounts, loading, error };
}

// ─── useMetrics ───────────────────────────────────────────────
export function useMetrics(accountId = "", platform = "") {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMetricsSummary(accountId, platform);
      setData(res.summary);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accountId, platform]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
}

// ─── useCampaigns ─────────────────────────────────────────────
export function useCampaigns(platform = "", dateRange = "LAST_30_DAYS", accountId = "") {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [lastSynced, setLastSynced] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCampaigns(platform, dateRange, accountId);
      setCampaigns(res.campaigns || []);
      setLastSynced(res.lastSynced);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [platform, dateRange, accountId]);

  useEffect(() => { fetch(); }, [fetch]);

  const sync = useCallback(async () => {
    setLoading(true);
    try {
      await syncCampaigns(dateRange);
      await fetch();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [dateRange, fetch]);

  return { campaigns, loading, error, lastSynced, refetch: fetch, sync };
}

// ─── useSortableData ──────────────────────────────────────────
export function useSortableData(items, defaultKey = "", defaultDir = "desc") {
  const [sortKey, setSortKey]   = useState(defaultKey);
  const [sortDir, setSortDir]   = useState(defaultDir);

  const sorted = useMemo(() => {
    if (!sortKey) return items;
    return [...items].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [items, sortKey, sortDir]);

  const requestSort = (key) => {
    if (key === sortKey) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  return { sorted, sortKey, sortDir, requestSort };
}

// ─── useTopPerformers ─────────────────────────────────────────
export function useTopPerformers(limit = 5) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTopPerformers(limit)
      .then((res) => setData(res.campaigns || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [limit]);

  return { data, loading };
}
